import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaEdit, FaExpand, FaCompress } from 'react-icons/fa';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import plugins from 'suneditor/src/plugins';
import { getWeeklyReport } from '../../../api/weeklyReport/getWeeklyReport';
import { addWeeklyReport, WeeklyReportAddPayload } from '../../../api/weeklyReport/addWeeklyReport';
import { updateWeeklyReport, WeeklyReportUpdatePayload } from '../../../api/weeklyReport/updateWeeklyReport';
import { listAllTournaments } from '../../../api/tournament/listAllTournaments';
import { getAllTournamentSeasons, TournamentSeason } from '../../../api/tournament/manageTournamentService';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import { DeleteModal } from '../../../Common/DeleteModal';
import { deleteWeeklyReport } from '../../../api/weeklyReport/deleteWeeklyReport';
import Select from 'react-select';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getImageUrl } from '../../../utils/imageUrl';

const SIDEBAR_WIDTH = 250;

interface TournamentObject {
  _id: string;
  name: string;
  sports?: string;
  startDate: string;
  endDate: string;
}

interface FormData extends Omit<WeeklyReportAddPayload, 'tournament'> {
  tournament: TournamentObject;
}

interface WeeklyReportFormProps {
  isEdit?: boolean;
  reportTitle?: string;
  onTitleChange?: (title: string) => void;
}

const WeeklyReportForm: React.FC<WeeklyReportFormProps> = ({ isEdit, reportTitle, onTitleChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [tournamentSeasons, setTournamentSeasons] = useState<TournamentSeason[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isMainImageUploading, setIsMainImageUploading] = useState(false);
  const [isRichTextImageUploading, setIsRichTextImageUploading] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    title: '',
    tournament: {
      _id: '',
      name: '',
      startDate: '',
      endDate: ''
    },
    desc: '',
    images: [],
    html: '',
    dateUploaded: '',
    tag: '',
  });

  // Add new state for initial form data and tracking changes
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Use reportTitle prop where needed for display
  const [reportTitleDisplay, setReportTitleDisplay] = useState<string>(reportTitle || 'Weekly Report');

  // Add state to store S3 paths
  const [s3ImagePaths, setS3ImagePaths] = useState<string[]>([]);

  // Add state for publish time
  const [publishTime, setPublishTime] = useState<string>('');
  const [isPublishNow, setIsPublishNow] = useState(false);

  // Add a ref for SunEditor instance
  const sunEditorRef = useRef<any>(null);

  // SunEditor options
  const editorOptions = {
    buttonList: [
      ['undo', 'redo'],
      ['font', 'fontSize', 'formatBlock'],
      ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
      ['removeFormat'],
      ['fontColor', 'hiliteColor'],
      ['indent', 'outdent'],
      ['align', 'horizontalRule', 'list', 'table'],
      ['link', 'video'],
      ['fullScreen', 'showBlocks', 'codeView'],
    ],
    plugins: plugins,
    height: '400px',
    imageUploadSizeLimit: 50 * 1024, // 50KB
    imageUploadUrl: `${process.env.REACT_APP_API_BASE_URL}/api/v1/general/presigned-url`,
    imageUploadHandler: async (file: File, callback: (url: string) => void) => {
      try {
        setIsUploading(true);
        // Compress the image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.05, // 50KB
          maxWidthOrHeight: 800,
          useWebWorker: true,
          initialQuality: 0.9
        });

        const dateObj = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
        const ext = compressedFile.type.split('/')[1] || 'jpg';
        const fileName = generateFilePath(formData.title, dateObj, ext);
        const context = generateContext(dateObj);

        const response = await secureApi.post(
          `/api/v1/general/presigned-url`,
          {
            fileName,
            contentType: compressedFile.type,
            context
          }
        );

        const presignedUrl = response.data?.presignedUrl;
        const s3Key = response.data?.key;

        if (!presignedUrl) {
          throw new Error('Failed to get presigned URL');
        }

        // Upload to S3
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': compressedFile.type },
          body: compressedFile
        });

        if (uploadResponse.ok) {
          const imageUrl = getImageUrl(s3Key);
          setS3ImagePaths(prev => [...prev, s3Key]);
          callback(imageUrl);
        } else {
          throw new Error('Failed to upload image');
        }
      } catch (err) {
        console.error('Image upload failed:', err);
        alert('Image upload failed. Please try again.');
      } finally {
        setIsUploading(false);
      }
    },
    videoUploadSizeLimit: 100 * 1024, // 100MB in KB (SunEditor expects KB, not bytes)
    videoUploadUrl: `${process.env.REACT_APP_API_BASE_URL}/api/v1/general/presigned-url`,
    videoFileInput: true,
    videoUploadInput: true,
    videoUpload: true,
    videoUploadRemoteUrls: false, // Disable URL input, only allow file upload
    videoUploadHandler: async (file: File, callback: (url: string) => void) => {
      try {
        setIsVideoUploading(true);
        
        // Validate file type
        const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/avi'];
        if (!allowedTypes.includes(file.type)) {
          throw new Error('Invalid video format. Please upload MP4, WebM, MOV, or AVI files.');
        }

        // Validate file size (100MB max)
        const MAX_FILE_SIZE_MB = 100;
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          throw new Error(`Video file is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        }

        const dateObj = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
        const ext = file.type.split('/')[1] || 'mp4';
        const fileName = generateFilePath(formData.title, dateObj, ext);
        const context = generateContext(dateObj);

        const response = await secureApi.post(
          `/api/v1/general/presigned-url`,
          {
            fileName,
            contentType: file.type,
            context
          }
        );

        const presignedUrl = response.data?.presignedUrl;
        const s3Key = response.data?.key;

        if (!presignedUrl) {
          throw new Error('Failed to get presigned URL');
        }

        // Upload to S3
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        });

        if (uploadResponse.ok) {
          const videoUrl = getImageUrl(s3Key);
          // Insert video tag with proper format matching the sample response
          // Video is uploaded to S3 at this point, and we insert it into the editor
          const videoTag = `<p><video width="100%" controls preload="metadata" style="border-radius: 8px; outline: none; display: block; margin: 10px 0;">
  <source src="${videoUrl}" type="${file.type}">
  Your browser does not support the video tag.
</video></p>`;
          // Insert the video tag directly into the editor at cursor position
          if (sunEditorRef.current) {
            sunEditorRef.current.insertHTML(videoTag);
          }
          // Don't call callback as we've already inserted the HTML manually
          // callback(videoUrl);
        } else {
          throw new Error('Failed to upload video');
        }
      } catch (err: any) {
        console.error('Video upload failed:', err);
        alert(err.message || 'Video upload failed. Please try again.');
      } finally {
        setIsVideoUploading(false);
      }
    },
    videoAccept: 'video/*'
  };

  // Helper function to generate file path
  const generateFilePath = (title: string, date: Date, ext: string) => {
    const publishDateForPath = date.toISOString().slice(0, 10); // yyyy-mm-dd
    const sanitizedTitle = (title || 'untitled').replace(/[^a-zA-Z0-9-_]/g, '_');
    return `${sanitizedTitle}_${Date.now()}.${ext}`;
  };

  // Helper function to generate context
  const generateContext = (date: Date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `weeklyReports/${dd}-${mm}-${yy}`;
  };

  // Helper function to format date for display (IST)
  const formatDateForDisplay = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper function to format date for API (UTC)
  const formatDateForAPI = (date: Date) => {
    return date.toISOString();
  };

  // Helper function to get UTC ISO string from IST date
  function istToUTCISOString(date: Date) {
    // Create a new date object to avoid modifying the original
    const localDate = new Date(date);
    // Get the timezone offset in minutes
    const timezoneOffset = localDate.getTimezoneOffset();
    // Subtract the offset to get UTC time (note: we subtract because getTimezoneOffset returns negative for ahead of UTC)
    localDate.setMinutes(localDate.getMinutes() - timezoneOffset);
    return localDate.toISOString();
  }

  // Handle publish now click
  const handlePublishNow = () => {
    // Get current local time
    const now = new Date();
    // Format time as HH:mm
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    setPublishTime(`${hours}:${minutes}`);
    // Save as UTC ISO string (IST to UTC)
    const utcString = istToUTCISOString(now);
    console.log('handlePublishNow dateUploaded:', utcString);
    setFormData(prev => ({
      ...prev,
      dateUploaded: utcString
    }));
    setIsPublishNow(true);
  };

  // Handle date picker change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const now = new Date();
      // If selected date is today, set time to current time
      const isToday =
        now.getFullYear() === date.getFullYear() &&
        now.getMonth() === date.getMonth() &&
        now.getDate() === date.getDate();
      let selectedDate = new Date(date);
      if (isToday) {
        selectedDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
        setPublishTime(
          `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        );
      } else {
        selectedDate.setHours(0, 0, 0, 0);
        setPublishTime('00:00');
      }
      // Save as UTC ISO string
      const utcString = istToUTCISOString(selectedDate);
      console.log('handleDateChange dateUploaded:', utcString);
      setFormData(prev => ({
        ...prev,
        dateUploaded: utcString
      }));
      setIsPublishNow(false);
    } else {
      setFormData(prev => ({
        ...prev,
        dateUploaded: ''
      }));
      setPublishTime('');
      setIsPublishNow(false);
    }
  };

  // Handle time picker change
  const handleTimeChange = (time: string) => {
    setPublishTime(time);
    // Update dateUploaded with the selected time
    if (formData.dateUploaded) {
      let localDate = new Date(formData.dateUploaded);
      const [hours, minutes] = time.split(':');
      localDate.setHours(Number(hours), Number(minutes), 0, 0);
      const utcString = istToUTCISOString(localDate);
      console.log('handleTimeChange dateUploaded:', utcString);
      setFormData(prev => ({
        ...prev,
        dateUploaded: utcString
      }));
    }
  };

  // Helper to determine adaptive compression options
  function getCompressionOptions(file: File) {
    const sizeKB = file.size / 1024;
    // Use a larger max dimension for better quality
    const maxWidthOrHeight = 1600;
    if (sizeKB < 120) {
      // Small images: skip compression
      return null;
    } else {
      // High quality, slightly larger max size
      return { maxSizeMB: 0.15, maxWidthOrHeight, initialQuality: 0.92, useWebWorker: true };
    }
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setImageUploadError('The selected image is too large. Please choose an image smaller than 5MB.');
      return;
    } else {
      setImageUploadError(null);
    }

    try {
      setIsMainImageUploading(true);
      setUploadSuccess(false);

      // Adaptive compression
      const options = getCompressionOptions(file);
      let processedFile = file;
      if (options) {
        processedFile = await imageCompression(file, options);
      }

      const dateObj = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
      const ext = processedFile.type.split('/')[1] || 'jpg';
      const fileName = generateFilePath(formData.title, dateObj, ext);
      const context = generateContext(dateObj);

      // 3. Request a presigned URL and S3 key from the backend
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: processedFile.type,
          context
        }
      );

      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;

      if (!presignedUrl) {
        alert('Failed to get presigned URL from server.');
        return;
      }

      // 4. Upload the image to S3 using the presigned URL (PUT)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': processedFile.type },
        body: processedFile
      });

      // 5. Only after successful upload, update form state
      if (uploadResponse.ok) {
        const imageUrl = getImageUrl(s3Key);
        setImagePreview(imageUrl);
        setS3ImagePaths([s3Key]);
        setFormData(prev => ({
          ...prev,
          images: [s3Key]
        }));
        setUploadSuccess(true);
        setImageUploadError(null);
      } else {
        setImagePreview(null);
        setImageUploadError('Image upload to S3 failed. Please try again.');
      }
    } catch (err) {
      alert('Image upload failed. Please try again.');
      console.error(err);
      // Revert preview if there's an error
      setImagePreview(null);
      setImageUploadError('Image upload failed. Please try again.');
    } finally {
      setIsMainImageUploading(false);
    }
  };

  const handleCustomImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setImageUploadError('The selected image is too large. Please choose an image smaller than 5MB.');
      return;
    } else {
      setImageUploadError(null);
    }

    try {
      setIsRichTextImageUploading(true);
      // Adaptive compression
      const options = getCompressionOptions(file);
      let processedFile = file;
      if (options) {
        processedFile = await imageCompression(file, options);
      }
      const dateObj = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
      const ext = processedFile.type.split('/')[1] || 'jpg';
      const fileName = generateFilePath(formData.title, dateObj, ext);
      const context = generateContext(dateObj);
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: processedFile.type,
          context
        }
      );
      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;
      if (!presignedUrl) {
        alert('Failed to get presigned URL from server.');
        return;
      }
      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': processedFile.type },
        body: processedFile
      });
      if (uploadResponse.ok) {
        const imageUrl = getImageUrl(s3Key);
        // Insert image into SunEditor at cursor
        if (sunEditorRef.current) {
          sunEditorRef.current.insertHTML(`<img src="${imageUrl}" alt="image" style="max-width:100%;" />`);
        }
      } else {
        setImageUploadError('Image upload to S3 failed. Please try again.');
      }
    } catch (err) {
      alert('Image upload failed. Please try again.');
      console.error(err);
      setImageUploadError('Image upload failed. Please try again.');
    } finally {
      setIsRichTextImageUploading(false);
      // Reset the input value so the same file can be uploaded again if needed
      e.target.value = '';
    }
  };

  const handleCustomVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/avi'];
    if (!allowedTypes.includes(file.type)) {
      setImageUploadError('Invalid video format. Please upload MP4, WebM, MOV, or AVI files.');
      return;
    }

    // Validate file size (100MB max)
    const MAX_FILE_SIZE_MB = 100;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setImageUploadError(`Video file is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    } else {
      setImageUploadError(null);
    }

    try {
      setIsVideoUploading(true);
      const dateObj = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
      const ext = file.type.split('/')[1] || 'mp4';
      const fileName = generateFilePath(formData.title, dateObj, ext);
      const context = generateContext(dateObj);
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: file.type,
          context
        }
      );
      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;
      if (!presignedUrl) {
        alert('Failed to get presigned URL from server.');
        return;
      }
      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });
      // Insert video tag into SunEditor at cursor with proper format
      // Video is uploaded to S3 at this point (after successful uploadResponse.ok)
      if (uploadResponse.ok) {
        const videoUrl = getImageUrl(s3Key);
        // Video tag with controls for preview and proper styling
        const videoTag = `<p><video width="100%" controls preload="metadata" style="border-radius: 8px; outline: none; display: block; margin: 10px 0;">
  <source src="${videoUrl}" type="${file.type}">
  Your browser does not support the video tag.
</video></p>`;
        if (sunEditorRef.current) {
          sunEditorRef.current.insertHTML(videoTag);
        }
      } else {
        setImageUploadError('Video upload to S3 failed. Please try again.');
      }
    } catch (err: any) {
      alert(err.message || 'Video upload failed. Please try again.');
      console.error(err);
      setImageUploadError(err.message || 'Video upload failed. Please try again.');
    } finally {
      setIsVideoUploading(false);
      // Reset the input value so the same file can be uploaded again if needed
      e.target.value = '';
    }
  };

  const handleEditImage = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    // Fetch tournaments
    const fetchTournaments = async () => {
      try {
        const data = await listAllTournaments();
        setTournaments(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        setTournaments([]);
      }
    };
    fetchTournaments();
  }, []);

  useEffect(() => {
    const tournamentId = formData.tournament._id;
    if (!tournamentId || tournamentId === 'no-tournament') {
      setTournamentSeasons([]);
      setSelectedSeasonId('');
      return;
    }
    const fetchSeasons = async () => {
      setSeasonsLoading(true);
      try {
        const selectedTournament = tournaments.find((tournament) => tournament._id === tournamentId);
        const seasons = await getAllTournamentSeasons(tournamentId, selectedTournament?.sports);
        setTournamentSeasons(seasons);
      } catch (err) {
        setTournamentSeasons([]);
      } finally {
        setSeasonsLoading(false);
      }
    };
    fetchSeasons();
  }, [formData.tournament._id]);

  useEffect(() => {
    const fetchReport = async () => {
      const state = location.state as { id?: string; edit?: boolean; report?: any };
      
      if (state?.report) {
        // Use the report passed from the table
        const report = state.report;
        const newFormData = {
          title: report.title || '',
          tournament: report.tag ? { _id: 'no-tournament', name: 'No Tournament', startDate: '', endDate: '' } : (report.tournament || {
            _id: '',
            name: '',
            startDate: '',
            endDate: ''
          }),
          desc: report.desc || '',
          images: report.images || [],
          html: report.html || '',
          dateUploaded: report.dateUploaded || '',
          tag: report.tag || '',
        };
        setFormData(newFormData);
        setInitialFormData(newFormData); // Set initial form data
        // Pre-fill season if present
        if (report.tournamentSeason) {
          const seasonId = typeof report.tournamentSeason === 'string'
            ? report.tournamentSeason
            : report.tournamentSeason?._id || '';
          setSelectedSeasonId(seasonId);
          setInitialSelectedSeasonId(seasonId);
        }
        if (onTitleChange) {
          onTitleChange(report.title || '');
        }
        // Set report ID for deep link URL
        if (report._id) {
          setReportId(report._id);
        }
        if (report.images && report.images.length > 0) {
          setS3ImagePaths(report.images);
          // Set the image preview using the S3 URL
          const imageUrl = getImageUrl(report.images[0]);
          setImagePreview(imageUrl);
        }
        // Set publish time if it exists
        if (report.publishTime) {
          setPublishTime(report.publishTime);
        } else if (report.dateUploaded) {
          // Extract IST time from dateUploaded
          const istDate = new Date(report.dateUploaded);
          const hours = String(istDate.getHours()).padStart(2, '0');
          const minutes = String(istDate.getMinutes()).padStart(2, '0');
          setPublishTime(`${hours}:${minutes}`);
        }
        setLoading(false);
        return;
      }
      if (state?.id) {
        try {
          setLoading(true);
          // Set report ID immediately for deep link URL
          setReportId(state.id);
          const response = await getWeeklyReport(state.id);
          if (response.status === 'success' && response.data) {
            const report = response.data;
            const newFormData = {
              title: report.title || '',
              tournament: report.tag ? { _id: 'no-tournament', name: 'No Tournament', startDate: '', endDate: '' } : (report.tournament || {
                _id: '',
                name: '',
                startDate: '',
                endDate: ''
              }),
              desc: report.desc || '',
              images: report.images || [],
              html: report.html || '',
              dateUploaded: report.dateUploaded || '',
              tag: report.tag || '',
            };
            setFormData(newFormData);
            setInitialFormData(newFormData); // Set initial form data
            // Pre-fill season if present
            if (report.tournamentSeason) {
              const seasonId = typeof report.tournamentSeason === 'string'
                ? report.tournamentSeason
                : report.tournamentSeason?._id || '';
              setSelectedSeasonId(seasonId);
              setInitialSelectedSeasonId(seasonId);
            }
            if (onTitleChange) {
              onTitleChange(report.title || '');
            }
            // Ensure report ID is set
            if (report._id) {
              setReportId(report._id);
            }
            if (report.images && report.images.length > 0) {
              setS3ImagePaths(report.images);
              // Set the image preview using the S3 URL
              const imageUrl = getImageUrl(report.images[0]);
              setImagePreview(imageUrl);
            }
            // Set publish time if it exists
            if (report.publishTime) {
              setPublishTime(report.publishTime);
            } else if (report.dateUploaded) {
              // Extract IST time from dateUploaded
              const istDate = new Date(report.dateUploaded);
              const hours = String(istDate.getHours()).padStart(2, '0');
              const minutes = String(istDate.getMinutes()).padStart(2, '0');
              setPublishTime(`${hours}:${minutes}`);
            }
          }
        } catch (error) {
          console.error('Error fetching report:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchReport();
  }, [location.state]);

  useEffect(() => {
    if (onTitleChange) {
      onTitleChange(formData.title);
    }
  }, [formData.title, onTitleChange]);

  // Helper: Convert File/Blob to base64
  function fileToBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Helper: Convert base64 to Blob
  function base64ToBlob(base64: string, type: string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get the selected date in UTC
      const selectedDate = formData.dateUploaded ? new Date(formData.dateUploaded) : new Date();
      // Format publish date as yyyy-MM-dd
      const publishDate = selectedDate.toISOString().slice(0, 10);
      // Generate dateUploaded for payload
      const dateUploaded = isPublishNow
        ? new Date().toISOString() // Instant publish uses current UTC time
        : getDateUploadedUTC(publishDate, publishTime); // Scheduled uses selected date/time
      // Create base payload
      const payload: Partial<WeeklyReportAddPayload> = {
        title: formData.title,
        desc: formData.desc,
        html: formData.html,
        // Only use uploaded main image, or (if editing) keep the old one. Never fallback to rich text images.
        images: s3ImagePaths.length > 0 ? s3ImagePaths : (isEdit ? formData.images : []),
        publishDate,
        publishTime: isPublishNow
          ? `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`
          : (publishTime || (location.state as any)?.report?.publishTime || ''),
        dateUploaded, // Use current UTC for instant, or scheduled UTC for future
      };

      // Only include tournament if a valid tournament is selected
      if (formData.tournament._id && formData.tournament._id !== 'no-tournament') {
        payload.tournament = formData.tournament._id; // Send only the tournament ID
        payload.tournamentSeason = selectedSeasonId;
      }

      // Only include tag if "No Tournament" is selected
      if (formData.tournament._id === 'no-tournament') {
        payload.tag = formData.tag;
      }

      if (location.state?.id || (location.state as any)?.report?._id) {
        const reportId = location.state?.id || (location.state as any)?.report?._id;
        await updateWeeklyReport(reportId, payload as Omit<WeeklyReportUpdatePayload, '_id'>);
        // Update reportId state after successful update
        setReportId(reportId);
      } else {
        const response = await addWeeklyReport(payload as WeeklyReportAddPayload);
        // Set report ID from response if available
        if (response?.data?._id) {
          setReportId(response.data._id);
        }
      }

      navigate('/WeeklyReports');
    } catch (error) {
      console.error('Error saving report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form validation: enable submit only if all required fields are filled
  const isFormValid = () => {
    const hasRealTournament = !!(formData.tournament._id && formData.tournament._id !== 'no-tournament');
    return !!(
      formData.title.trim() &&
      formData.desc.trim() &&
      formData.html.trim() &&
      formData.dateUploaded &&
      (
        (hasRealTournament && selectedSeasonId) ||
        (formData.tournament._id === 'no-tournament' && formData.tag?.trim())
      ) &&
      // Only allow submit if a main image is uploaded, or (if editing) an old image exists
      (s3ImagePaths.length > 0 || (isEdit && formData.images && formData.images.length > 0))
    );
  };

  // Helper to robustly parse both YYYY-MM-DD and ISO strings
  function parseDateString(dateStr: string | undefined | null) {
    if (!dateStr) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return new Date(dateStr + 'T00:00:00');
    }
    return new Date(dateStr);
  }

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const reportId = location.state?.id || (location.state as any)?.report?._id;
      await deleteWeeklyReport(reportId);
      navigate('/WeeklyReports');
    } catch (error) {
      console.error('Error deleting report:', error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Helper to generate UTC ISO string from publishDate and publishTime in IST
  function getDateUploadedUTC(publishDate: string, publishTime: string) {
    if (!publishDate || !publishTime) return '';
    const [day, month, year] = publishDate.split('-').map(Number);
    const [hours, minutes] = publishTime.split(':').map(Number);
    // Create a Date object in IST
    const istDate = new Date(year, month - 1, day, hours, minutes);
    return istDate.toISOString();
  }

  // Add function to compare form data
  const compareFormData = (current: FormData, initial: FormData | null, currentSeasonId: string, initialSeasonId: string) => {
    if (!initial) return false;
    
    return (
      current.title !== initial.title ||
      current.desc !== initial.desc ||
      current.html !== initial.html ||
      current.tag !== initial.tag ||
      current.dateUploaded !== initial.dateUploaded ||
      current.tournament._id !== initial.tournament._id ||
      JSON.stringify(current.images) !== JSON.stringify(initial.images) ||
      currentSeasonId !== initialSeasonId
    );
  };

  // Add state to track initial selected season for change detection
  const [initialSelectedSeasonId, setInitialSelectedSeasonId] = useState<string>('');

  // Add useEffect to track changes
  useEffect(() => {
    setHasChanges(compareFormData(formData, initialFormData, selectedSeasonId, initialSelectedSeasonId));
  }, [formData, initialFormData, selectedSeasonId, initialSelectedSeasonId]);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-8">
            <ShimmerLoader type="form" />
          </div>
          <div className="col-md-4">
            <ShimmerLoader type="image" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8">
            <div className="mb-3">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Enter report title"
              />
            </div>
            <div className="mb-3 d-flex align-items-center" style={{ gap: 12 }}>
              <label className="form-label mb-0" style={{ minWidth: 120, marginRight: 8 }}>Published Date</label>
              <DatePicker
                selected={formData.dateUploaded ? new Date(formData.dateUploaded) : null}
                onChange={handleDateChange}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                placeholderText="Select publish date"
                isClearable
                showTimeSelect={false}
                popperPlacement="right-start"
              />
              {!isPublishNow && formData.dateUploaded && (
                <input
                  type="time"
                  className="form-control"
                  value={publishTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  style={{ width: '120px' }}
                />
              )}
              <button
                type="button"
                className="btn btn-primary ms-2"
                onClick={handlePublishNow}
              >
                Publish Now
              </button>
            </div>
            {/* Tournament dropdown and Tag field logic */}
            {tournaments.length > 0 ? (
              <>
                <div className="mb-3">
                  <label className="form-label">Tournament</label>
                  <Select
                    classNamePrefix="react-select"
                    name="tournamentId"
                    value={(() => {
                      if (formData.tournament._id === 'no-tournament') {
                        return { value: 'no-tournament', label: 'No Tournament', _id: 'no-tournament' };
                      }
                      const found = tournaments.find(t => t._id === formData.tournament._id);
                      return found ? { value: found._id, label: found.name, _id: found._id } : null;
                    })()}
                    onChange={option => {
                      setSelectedSeasonId('');
                      if (!option) {
                        setFormData(prev => ({ ...prev, tournament: { _id: '', name: '', startDate: '', endDate: '' }, tag: '' }));
                      } else if (option.value === 'no-tournament') {
                        setFormData(prev => ({ ...prev, tournament: { _id: 'no-tournament', name: 'No Tournament', startDate: '', endDate: '' }, tag: prev.tag || '' }));
                      } else {
                        const selected = tournaments.find(t => t._id === option.value);
                        setFormData(prev => ({ ...prev, tournament: selected || { _id: '', name: '', startDate: '', endDate: '' }, slug: '', tag: '' }));
                      }
                    }}
                    options={[
                      { value: 'no-tournament', label: 'No Tournament', _id: 'no-tournament' },
                      ...tournaments.map(t => ({ value: t._id, label: t.name, _id: t._id }))
                    ]}
                    placeholder="Select Tournament"
                    isClearable
                    styles={{
                      control: (base) => ({ ...base, minHeight: '38px' }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
                {/* Show Tournament Season dropdown only when a real tournament is selected */}
                {formData.tournament._id && formData.tournament._id !== 'no-tournament' && (
                  <div className="mb-3">
                    <label className="form-label">Tournament Season</label>
                    <Select
                      classNamePrefix="react-select"
                      name="tournamentSeason"
                      value={
                        selectedSeasonId
                          ? (() => {
                              const s = tournamentSeasons.find(s => s._id === selectedSeasonId);
                              return s ? { value: s._id, label: s.seasonLabel || s.seasonType } : null;
                            })()
                          : null
                      }
                      onChange={option => setSelectedSeasonId(option ? option.value : '')}
                      options={tournamentSeasons.map(s => ({ value: s._id, label: s.seasonLabel || s.seasonType }))}
                      placeholder={seasonsLoading ? 'Loading seasons...' : 'Select Tournament Season'}
                      isLoading={seasonsLoading}
                      isDisabled={seasonsLoading || tournamentSeasons.length === 0}
                      isClearable
                      styles={{
                        control: (base) => ({ ...base, minHeight: '38px' }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                )}
                {/* Show Tag field only if "No Tournament" is selected */}
                {formData.tournament._id === 'no-tournament' && (
                  <div className="mb-3">
                    <label className="form-label">Tag</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.tag || ''}
                      onChange={e => setFormData(prev => ({ ...prev, tag: e.target.value }))}
                      placeholder="Enter tag for non-tournament report"
                      required
                    />
                  </div>
                )}
              </>
            ) : (
              // fallback for when tournaments are not loaded
              <div className="mb-3">
                <label className="form-label">Tag</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.tag || ''}
                  onChange={e => setFormData(prev => ({ ...prev, tag: e.target.value }))}
                  placeholder="Enter tag for non-tournament report"
                  required
                />
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                value={formData.desc}
                onChange={e => setFormData(prev => ({ ...prev, desc: e.target.value }))}
                rows={4}
                required
                placeholder="Enter a brief description"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Content</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => document.getElementById('custom-suneditor-image-input')?.click()}
                >
                  Upload Image
                </button>
                <input
                  id="custom-suneditor-image-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleCustomImageUpload}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => document.getElementById('custom-suneditor-video-input')?.click()}
                >
                  Upload Video
                </button>
                <input
                  id="custom-suneditor-video-input"
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={handleCustomVideoUpload}
                />
                {isRichTextImageUploading && <span style={{ color: '#007bff' }}>Uploading image...</span>}
                {isVideoUploading && <span style={{ color: '#007bff' }}>Uploading video...</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <SunEditor
                  setContents={formData.html}
                  onChange={(content) => setFormData(prev => ({ ...prev, html: content }))}
                  setOptions={editorOptions}
                  getSunEditorInstance={editor => { sunEditorRef.current = editor; }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 24, paddingBottom: 20 }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !isFormValid() || (!hasChanges && isEdit)}
              >
                {isSubmitting ? 'Submitting...' : isEdit ? 'Update' : 'Submit'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(true)}
              >
                Preview
              </button>
              {isEdit && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/WeeklyReports')}
              >
                Cancel
              </button>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Upload Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <div className="mt-3">
                <div style={{
                  position: 'relative',
                  width: 300,
                  height: 200,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid #dee2e6',
                  background: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {imagePreview ? (
                    <>
                      <img
                        src={getImageUrl(imagePreview)}
                        alt="Preview"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          borderRadius: 8 
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleEditImage}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'rgba(255,255,255,0.8)',
                          border: 'none',
                          borderRadius: '50%',
                          padding: 4,
                          cursor: 'pointer',
                          zIndex: 2
                        }}
                        title="Edit Image"
                      >
                        <FaEdit size={18} color="#007bff" />
                      </button>
                    </>
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 20,
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="bi bi-cloud-upload display-4"></i>
                      <p className="mt-2 mb-0">
                        Drag and drop an image here, or click to select
                      </p>
                      <small className="text-muted">
                        Max size: 5MB, Supported formats: JPEG, PNG
                      </small>
                    </div>
                  )}
                  {isMainImageUploading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255,255,255,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        zIndex: 3
                      }}
                    >
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0">Uploading image...</p>
                    </div>
                  )}
                  {uploadSuccess && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(40,167,69,0.9)',
                        color: 'white',
                        padding: '8px',
                        textAlign: 'center',
                        zIndex: 3
                      }}
                    >
                      Image has been added successfully
                    </div>
                  )}
                </div>
                {/* Error message below uploader box */}
                {imageUploadError && (
                  <div style={{ color: 'red', marginTop: 8, fontSize: 14 }}>
                    {imageUploadError}
                  </div>
                )}
              </div>
            </div>
            {/* Deep Link URL Field - Only show when report ID exists */}
            {reportId && (
              <div className="mb-3">
                <label className="form-label">Deep Link URL</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    className="form-control"
                    value={`sfl://app/viewWeeklyReport?reportId=${reportId}`}
                    readOnly
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      cursor: 'text',
                      fontFamily: 'monospace',
                      fontSize: '13px'
                    }}
                    onClick={(e) => {
                      // Select all text when clicked for easy copying
                      (e.target as HTMLInputElement).select();
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={async () => {
                      const url = `sfl://app/viewWeeklyReport?reportId=${reportId}`;
                      try {
                        await navigator.clipboard.writeText(url);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      } catch (err) {
                        console.error('Failed to copy:', err);
                        // Fallback: select text
                        const input = document.createElement('input');
                        input.value = url;
                        document.body.appendChild(input);
                        input.select();
                        document.execCommand('copy');
                        document.body.removeChild(input);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }
                    }}
                    title={copySuccess ? "Copied!" : "Copy URL"}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '38px',
                      height: '38px',
                      padding: 0
                    }}
                  >
                    {copySuccess ? (
                      <i className="bi bi-check" style={{ fontSize: '18px' }}></i>
                    ) : (
                      <i className="bi bi-clipboard" style={{ fontSize: '18px' }}></i>
                    )}
                  </button>
                </div>
                {copySuccess && (
                  <div style={{ color: '#28a745', marginTop: 8, fontSize: 14, fontWeight: 500 }}>
                    <i className="bi bi-check-circle" style={{ marginRight: 4 }}></i>
                    URL copied to clipboard!
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </form>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Weekly Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <h2 style={{ fontWeight: 700, marginBottom: 16 }}>{formData.title}</h2>
            <div style={{ marginBottom: 16 }}>
              <strong>Tournament:</strong> {formData.tournament?.name || '-'}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Publish At:</strong> {formData.dateUploaded ? (() => {
                const date = new Date(formData.dateUploaded);
                const dateStr = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
                const timeStr = publishTime || date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
                return `${dateStr} ${timeStr}`;
              })() : 'Draft'}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Description:</strong>
              <div style={{ marginTop: 8 }}>{formData.desc}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>HTML Content:</strong>
              <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: formData.html }} />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Weekly Report"
        message={`Are you sure you want to delete the weekly report "${formData.title}"? This action cannot be undone.`}
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </div>
  );
};

export default WeeklyReportForm; 